import React, { Suspense, useCallback, useMemo } from 'react';
import { useSources } from 'src/hooks/api';
import type { FeedbackEnum, Source, SourceURLFeedback } from 'src/types';
import { sortByPosition } from 'src/utils/sortByPosition';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import DefaultSource from './Source/DefaultSource';
import SourceCard from './Source/SourceCard';

import './Sources.sass';

interface SourcesProps {
	sources: Source[];
	messageId: string;
	internet: boolean;
	handleSourceURLFeedback?: (newSourceUrlFeedback: SourceURLFeedback) => void;
	sourceFeedback?: SourceURLFeedback[];
}

const SourcesList = ({ sources, messageId, internet, handleSourceURLFeedback, sourceFeedback }: SourcesProps) => {
	const sourcesUrl = useMemo(() => sources?.map((s) => s.url), [sources]);
	const uniqueCacheKey = internet ? `${messageId}-internet` : `${messageId}-normal`;

	const fullSources = useSources(sourcesUrl, uniqueCacheKey);

	const createSelectFeedback = useCallback(
		(originalSource: Source) => {
			if (!handleSourceURLFeedback) return undefined;
			return (feedback: FeedbackEnum | undefined) => {
				handleSourceURLFeedback({
					id: originalSource.id,
					url: originalSource.url,
					feedback
				});
			};
		},
		[handleSourceURLFeedback]
	);

	const renderSource = useMemo(() => {
		const combinedSources = fullSources?.map((source) => {
			const originalSource = sources?.find((i) => i.url === source?.url);
			return {
				...source,
				position: originalSource?.position,
				isOutdated: originalSource?.outdated,
				originalSourceData: originalSource
			};
		});

		const sortedSources = sortByPosition(combinedSources);

		return sortedSources?.map((source, ind) => {
			const selectSourceUrlFeedback = createSelectFeedback(source.originalSourceData);
			const sourceUrlFeedback = sourceFeedback?.find((s) => s.url === source.url)?.feedback;

			return (
				<SourceCard
					isInternet={internet}
					isUnused={!source.position}
					isOutdated={source.isOutdated}
					source={source}
					index={source.position}
					key={source?.url + ind}
					selectSourceURLFeedback={selectSourceUrlFeedback}
					sourceURLFeedback={sourceUrlFeedback}
				/>
			);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fullSources, sources, createSelectFeedback, sourceFeedback]);

	return <>{renderSource}</>;
};

const SourcesWrapper = ({ sources, messageId, handleSourceURLFeedback, internet, sourceFeedback }: SourcesProps) => {
	return (
		<ErrorBoundary fallback={<div />}>
			<Suspense
				fallback={
					<>
						{sources?.map((_, index) => (
							<DefaultSource loading key={index} />
						))}
					</>
				}
			>
				<SourcesList
					key={internet ? 'internet-sources-list' : 'sources-list'}
					sources={sources}
					messageId={messageId}
					internet={internet}
					handleSourceURLFeedback={handleSourceURLFeedback}
					sourceFeedback={sourceFeedback}
				/>
			</Suspense>
		</ErrorBoundary>
	);
};

export default React.memo(SourcesWrapper);
